import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Images } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { uploadMedia } from "@/lib/media";
import { mediaSrc } from "@/lib/img";
import MediaPicker from "@/components/admin/MediaPicker";

export default function MediaField({ label, value, onChange, accept = "image", testKey, compact = false }) {
  const [picking, setPicking] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    setBusy(true);
    try {
      const item = await uploadMedia(file, setProgress);
      onChange(item.url);
      toast.success("Uploaded to library");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const inputAccept = accept === "video" ? "video/*" : accept === "any" ? "image/*,video/*" : "image/*";
  const isImage = typeof value === "string" && value && !/\.(mp4|webm|mov|m4v)(\?|$)/i.test(value);

  return (
    <div className={compact ? "" : "md:col-span-2"}>
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      <div className={`${label ? "mt-1.5" : ""} flex items-center gap-2`}>
        {value && isImage ? (
          <img src={mediaSrc(value, 320)} alt="" loading="lazy" className="h-11 w-11 shrink-0 rounded border border-border bg-muted object-cover" />
        ) : null}
        <Input
          value={typeof value === "string" ? value : ""}
          placeholder="Paste a URL or pick from the library"
          data-testid={`field-${testKey}-url`}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid={`field-${testKey}-library`}
          onClick={() => setPicking(true)}
          className="btn-outline h-10 shrink-0 px-3 text-xs"
        >
          <Images className="h-3.5 w-3.5" /> Library
        </button>
        <label className="btn-outline h-10 shrink-0 cursor-pointer px-3 text-xs">
          <UploadCloud className="h-3.5 w-3.5" />
          {busy ? `${progress}%` : "Upload"}
          <input
            type="file"
            accept={inputAccept}
            className="hidden"
            disabled={busy}
            data-testid={`field-${testKey}-upload`}
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          />
        </label>
      </div>
      <MediaPicker open={picking} onOpenChange={setPicking} accept={accept} onPick={onChange} />
    </div>
  );
}
